/**
 * Delivery estimate engine (FND-803).
 *
 * Ported from the Bulldog Targets build, but rewritten as dependency-free
 * vanilla JS (Bulldog uses React + date-fns) and computed entirely in the
 * store's timezone via Intl, rather than from a server-rendered timestamp.
 * That matters because Shopify full-page caching can serve a stale "now",
 * which would silently freeze the estimate on a cached page.
 *
 * Exposes window.USAMMDelivery so the Algolia hit template in main.js can
 * reuse exactly the same maths as the product page.
 */
(function () {
  const DEFAULT_TIME_ZONE = 'America/Chicago';
  const DEFAULT_CUTOFF = { hour: 11, minute: 0 };

  /**
   * Wall-clock date and time in a given timezone.
   * @param {number} timestamp - Epoch milliseconds.
   * @param {string} timeZone - IANA timezone name.
   * @returns {object} Calendar parts in that zone.
   */
  function zonedParts(timestamp, timeZone) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });

    const parts = {};
    formatter.formatToParts(new Date(timestamp)).forEach((part) => {
      parts[part.type] = part.value;
    });

    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      // Intl can emit hour "24" for midnight under hour12: false.
      hour: parts.hour === '24' ? 0 : Number(parts.hour),
      minute: Number(parts.minute),
      second: Number(parts.second)
    };
  }

  /**
   * The timezone's UTC offset at a given instant, in milliseconds.
   * @param {number} timestamp - Epoch milliseconds.
   * @param {string} timeZone - IANA timezone name.
   * @returns {number} Offset in ms.
   */
  function offsetAt(timestamp, timeZone) {
    const whole = Math.floor(timestamp / 1000) * 1000;
    const p = zonedParts(whole, timeZone);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - whole;
  }

  /**
   * Converts a wall-clock time in a timezone to an absolute instant.
   * Resolved in two passes so the offset is correct across DST boundaries,
   * where the first guess can land on the wrong side of a transition.
   * @param {object} date - Civil date { year, month, day }.
   * @param {object} time - { hour, minute }.
   * @param {string} timeZone - IANA timezone name.
   * @returns {number} Epoch milliseconds.
   */
  function zonedToTimestamp(date, time, timeZone) {
    const guess = Date.UTC(date.year, date.month - 1, date.day, time.hour, time.minute, 0);
    const firstOffset = offsetAt(guess, timeZone);
    const candidate = guess - firstOffset;
    const secondOffset = offsetAt(candidate, timeZone);
    return secondOffset === firstOffset ? candidate : guess - secondOffset;
  }

  /* Civil-date helpers. Day arithmetic is done in UTC so it is never
   * disturbed by daylight saving shifts. */

  function toUtcDate(date) {
    return new Date(Date.UTC(date.year, date.month - 1, date.day));
  }

  function addDays(date, count) {
    const dt = toUtcDate(date);
    dt.setUTCDate(dt.getUTCDate() + count);
    return { year: dt.getUTCFullYear(), month: dt.getUTCMonth() + 1, day: dt.getUTCDate() };
  }

  /** @returns {boolean} Monday–Friday. USAMM ships business days only. */
  function isBusinessDay(date) {
    const day = toUtcDate(date).getUTCDay();
    return day >= 1 && day <= 5;
  }

  function nextBusinessDay(date) {
    let cursor = date;
    while (!isBusinessDay(cursor)) cursor = addDays(cursor, 1);
    return cursor;
  }

  /**
   * Parses a "HH:MM" cutoff, falling back to the default if malformed.
   * @param {string} value - Cutoff time in 24-hour form.
   * @returns {object} { hour, minute }.
   */
  function parseCutoff(value) {
    const match = /^\s*(\d{1,2}):(\d{2})\s*$/.exec(String(value || ''));
    if (!match) return DEFAULT_CUTOFF;

    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (hour > 23 || minute > 59) return DEFAULT_CUTOFF;
    return { hour, minute };
  }

  /**
   * Resolves the lead time for a product, preferring a metafield override.
   * @param {*} override - Per-product metafield value, if any.
   * @param {*} fallback - Store default.
   * @returns {number} Lead time in business days.
   */
  function resolveLeadDays(override, fallback) {
    const parsed = parseInt(override, 10);
    if (!Number.isNaN(parsed) && parsed >= 0) return parsed;

    const base = parseInt(fallback, 10);
    return Number.isNaN(base) || base < 0 ? 0 : base;
  }

  /**
   * Works out the delivery date and the moment ordering must happen by.
   *
   * Orders placed before the cutoff on a business day ship that day; anything
   * later (or on a weekend) rolls to the next business day, which is what
   * pushes the estimate out by a day. The lead time is then counted in
   * business days from the shipping day.
   *
   * @param {object} options - { timeZone, cutoff, leadDays, now }.
   * @returns {object} { deliveryDate, cutoffTimestamp, shipDate, leadDays }.
   */
  function compute(options) {
    const settings = options || {};
    const timeZone = settings.timeZone || DEFAULT_TIME_ZONE;
    const cutoff = parseCutoff(settings.cutoff);
    const leadDays = resolveLeadDays(settings.leadDays, 0);
    const now = settings.now == null ? Date.now() : settings.now;

    const current = zonedParts(now, timeZone);
    let shipDate = { year: current.year, month: current.month, day: current.day };

    const nowMinutes = current.hour * 60 + current.minute;
    const cutoffMinutes = cutoff.hour * 60 + cutoff.minute;

    if (!isBusinessDay(shipDate) || nowMinutes >= cutoffMinutes) {
      shipDate = nextBusinessDay(addDays(shipDate, 1));
    }

    let deliveryDate = shipDate;
    for (let i = 0; i < leadDays; i += 1) {
      deliveryDate = nextBusinessDay(addDays(deliveryDate, 1));
    }

    return {
      shipDate,
      deliveryDate,
      leadDays,
      cutoffTimestamp: zonedToTimestamp(shipDate, cutoff, timeZone)
    };
  }

  /**
   * Formats a civil date the way Bulldog does, e.g. "Tuesday, Sep 08".
   * @param {object} date - Civil date.
   * @param {string} [locale] - BCP 47 locale.
   * @returns {string} Formatted date.
   */
  function formatDate(date, locale) {
    return new Intl.DateTimeFormat(locale || 'en-US', {
      timeZone: 'UTC',
      weekday: 'long',
      month: 'short',
      day: '2-digit'
    }).format(toUtcDate(date));
  }

  /**
   * Formats remaining time, using the largest sensible unit.
   *
   * Over a weekend the cutoff can be ~72 hours out, and "71 hours and 25
   * minutes" is hard to read at a glance, so a day or more is expressed as
   * days and hours. Minutes only matter once the cutoff is close.
   *
   * @param {number} ms - Milliseconds remaining.
   * @returns {string} Human-readable countdown, empty once elapsed.
   */
  function formatCountdown(ms) {
    if (!(ms > 0)) return '';

    const totalMinutes = Math.floor(ms / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    const plural = (value) => (value === 1 ? '' : 's');
    const parts = [];

    if (days > 0) {
      parts.push(`${days} day${plural(days)}`);
      if (hours > 0) parts.push(`${hours} hour${plural(hours)}`);
      return parts.join(' and ');
    }

    if (hours > 0) parts.push(`${hours} hour${plural(hours)}`);
    if (minutes > 0) parts.push(`${minutes} minute${plural(minutes)}`);
    if (!parts.length) return 'less than a minute';

    return parts.join(' and ');
  }

  /**
   * Renders a "{date}" / "{countdown}" template into an element as real nodes,
   * so merchant-entered text is never treated as markup.
   * @param {Element} el - Target element.
   * @param {string} template - Text containing {date} and/or {countdown}.
   * @param {object} values - { date, countdown }.
   */
  function renderTemplate(el, template, values) {
    const text = String(template || '');
    const pattern = /\{(date|countdown)\}/g;

    el.textContent = '';

    let lastIndex = 0;
    let match = pattern.exec(text);

    while (match !== null) {
      if (match.index > lastIndex) {
        el.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
      }

      const strong = document.createElement('strong');
      strong.className = `delivery-estimate__${match[1]}`;
      strong.textContent = values[match[1]] || '';
      el.appendChild(strong);

      lastIndex = pattern.lastIndex;
      match = pattern.exec(text);
    }

    if (lastIndex < text.length) {
      el.appendChild(document.createTextNode(text.slice(lastIndex)));
    }
  }

  window.USAMMDelivery = {
    compute,
    formatDate,
    formatCountdown,
    renderTemplate,
    resolveLeadDays,
    parseCutoff,
    zonedParts
  };

  if (!customElements.get('delivery-estimate')) {
    class DeliveryEstimate extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.output = this.querySelector('.js-delivery-estimate') || this;
        this.template = this.dataset.template || '';
        this.showCountdown = this.dataset.countdown === 'true';

        this.update();

        // Ticked every second so the countdown is genuinely live and the date
        // rolls forward the moment the cutoff passes while someone is reading.
        // update() only touches the DOM when the rendered text changes.
        if (this.showCountdown) {
          this.timer = setInterval(() => this.update(), 1000);
        }
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      update() {
        const result = compute({
          timeZone: this.dataset.timezone,
          cutoff: this.dataset.cutoff,
          leadDays: this.dataset.leadDays
        });

        const values = {
          date: formatDate(result.deliveryDate, this.dataset.locale),
          countdown: formatCountdown(result.cutoffTimestamp - Date.now())
        };

        const signature = `${values.date}|${values.countdown}`;
        if (signature === this.signature) return;
        this.signature = signature;

        renderTemplate(this.output, this.template, values);
        this.hidden = false;
      }
    }

    customElements.define('delivery-estimate', DeliveryEstimate);
  }
}());
